// GeneralErrorToastr.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { EMPTY_OBJECT, NULL_FUNCTION } from '../resource/defaults.js';
import { sentryLogger } from '../resource/sentry.js';
import DefaultConfirmation from './DefaultConfirmation.jsx';
import FadeOutToBottom from '../style/FadeOutToBottom.js';
import FadeInFromBottom from '../style/FadeInFromBottom.js';
import media from '../style/media.js';

const MAX_ERROR_DETAIL_LENGTH = 200;

const parseErrorObject = errorObject => {
  const { name = '', message = '' } =
    errorObject && typeof errorObject === 'object' ? errorObject : {};
  const errorDetail = message
    ? `[${name}] ${message.replace(/\n/g, ' ')}`
    : JSON.stringify(errorObject || '').slice(0, MAX_ERROR_DETAIL_LENGTH);

  return {
    errorName: name,
    isNetworkingIssue: name === 'ChunkLoadError',
    errorDetail,
  };
};

export class GeneralErrorToastr extends React.PureComponent {
  state = { isServer: true, show: true };
  nextTick = null;

  componentDidMount() {
    const { errorName, errorDetail } = parseErrorObject(this.props.errorObject);
    sentryLogger.error('error_toastr_general', {
      errorName,
      errorDetail,
    });
    this.nextTick = setTimeout(() => this.setState({ isServer: false }), 0);
  }

  componentWillUnmount() {
    clearTimeout(this.nextTick);
  }

  render() {
    const { isServer, show } = this.state;
    const { durationSec, errorObject, t, retry } = this.props;
    const { errorName, errorDetail, isNetworkingIssue } =
      parseErrorObject(errorObject);

    if (isServer) {
      return <div className="error-placeholder" />;
    }

    if (!show) {
      return null;
    }

    return (
      <AnimatedWrapper $durationSec={durationSec} $show={show}>
        <DefaultConfirmation
          type="error"
          shouldShowIcon={false}
          title={t(
            isNetworkingIssue
              ? 'retry_failed_reason'
              : errorName || 'something_went_wrong',
            {
              ns: TranslationNamespace.NOTIFICATION,
            }
          )}
          content={errorDetail}
          primaryButtonText={t('retry', {
            ns: TranslationNamespace.DEFAULT,
          })}
          onPrimaryButtonClick={retry}
          secondaryButtonText={t('general_cancel', {
            ns: TranslationNamespace.GENERAL,
          })}
          onSecondaryButtonClick={() => this.setState({ show: false })}
        />
      </AnimatedWrapper>
    );
  }
}

GeneralErrorToastr.propTypes = {
  errorObject: PropTypes.object,
  durationSec: PropTypes.number,
  t: PropTypes.func.isRequired,
  retry: PropTypes.func,
};

GeneralErrorToastr.defaultProps = {
  errorObject: EMPTY_OBJECT,
  durationSec: 0.6,
  retry: NULL_FUNCTION,
};

const AnimatedWrapper = styled.div`
  position: fixed;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  margin: 0 12px;
  z-index: 199;

  ${media.tablet`
    margin: 0 auto;
  `}

  ${media.mobile`
    margin: 0 12px;
  `}

  animation: ${({ $durationSec }) => $durationSec}s forwards
    ${({ $show }) => ($show ? FadeInFromBottom : FadeOutToBottom)};
`;

export default withTranslation()(GeneralErrorToastr);
